import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trash2, Crosshair, Smile, Frown, Plus } from 'lucide-react';
import Select from '../../components/Select';

import Header from '../../components/Header';
import Tabs from '../../components/Tabs';
import Textarea from '../../components/Textarea';
import Actions from '../../components/Actions';
import Button from '../../components/Button';
import List from '../../components/List';
import Line from '../../components/Line';
import Text from '../../components/Text';
import Title from '../../components/Title';
import { SUSTAINABILITY_MAX_COUNT, SELECT_STYLES } from '../../utils/const';
import Goals from './Goals';
import ConfirmationModal from '../../components/ConfirmationModal';
import TabsCut from '../../components/TabsCut';
import TabsList from '../../components/TabsList';
import TabsListItem from '../../components/TabsListItem';
import { selectLabel, selectValue } from '../../utils/helpers';
import MobileMenuHeader from '../../components/MobileMenuHeader';
import TabsListTitle from '../../components/TabsListTitle';

const strategyFields = ['sdg', 'problem', 'value'];
const caseStudyFields = ['sdgName', 'problem', 'value'];

export default function Strategy(props) {
  const {
    nextClickHandler,
    menuItems,
    selectComponent,
    componentIndex,
    rules,
    backPath,
    locales,
    locales: {
      common,
      tipCustomerSegment,
      caseStudies,
      sustainability: {
        name,
        strategy: { header, title, text, list, tabs },
        sdgs,
      },
    },
  } = props;

  const tabLabelForField = (field) => {
    if (field === 'sdg' || field === 'sdgName') return tabs.title || tabs.sdg;
    return tabs[field];
  };
  const { collection } = useSelector((state) => state.sustainability);
  const {
    sustainability: {
      addSustainability,
      updateSustainability,
      removeSustainability,
    },
  } = useDispatch();
  const sustainabilitySdgOptions = sdgs.map((e, index) => ({
    label: e.title,
    value: (index + 1).toString(),
  }));
  const [opened, setOpened] = useState(false);
  const mobileHeader = (
    <MobileMenuHeader icon="SustainabilityCanvas" title={name} color="violet" />
  );

  const getTableHeaderIconByIndex = (index, startIndex) => {
    // eslint-disable-next-line default-case
    let virtualIndex = index;

    if (startIndex) {
      virtualIndex = startIndex;
    }

    // eslint-disable-next-line default-case
    switch (virtualIndex) {
      case 0:
        return <Crosshair className="w-6 h-auto" />;
      case 1:
        return <Smile className="w-6 h-auto" />;
      case 2:
        return <Frown className="w-6 h-auto" />;
    }
  };

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon="icon-sustainability"
        locales={locales}
        componentIndex={componentIndex}
        className="bmc-page-header--violet"
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        mobileMenuHeader={mobileHeader}
        rules={rules}
        backPath={backPath}
      />

      <Text>{[text[0]]}</Text>

      <Goals locales={locales} />

      <Title text={title[0]} />

      <List
        items={list}
        type="horizontal"
        icons={(index) => getTableHeaderIconByIndex(index + 1)}
      />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--violet">{title[1]}</span> {title[2]}
      </h2>

      <Text align="center">{[text[1]]}</Text>

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-60 bmc-page-tabs--violet"
      >
        {[
          <div key="strategy">
            <div>
              {collection.map((item, index) => (
                <TabsList
                  key={item.id}
                  divider={index < SUSTAINABILITY_MAX_COUNT - 1}
                >
                  <TabsListTitle className="flex flex-row justify-between items-center">
                    {tabs.goalTitle} {index + 1}
                    {collection.length > 1 && (
                      <ConfirmationModal
                        className="ml-3 hover:bg-muted-lighter/60 text-muted"
                        body={locales.common.deleteConfirmation}
                        confirmText="Delete"
                        buttonAriaLabel="Delete"
                        onConfirm={() =>
                          removeSustainability({ index, id: item.id })
                        }
                      >
                        <Trash2 className="w-6 min-w-6 h-auto" />
                      </ConfirmationModal>
                    )}
                  </TabsListTitle>

                  {strategyFields.map((field, i) => (
                    <TabsListItem
                      divider={false}
                      className="mb-5"
                      key={i}
                      icon={getTableHeaderIconByIndex(i)}
                      title={tabLabelForField(field)}
                      text={
                        i === 0 ? (
                          <Select
                            styles={SELECT_STYLES}
                            className="text-black"
                            options={sustainabilitySdgOptions}
                            value={selectValue(
                              item[field],
                              selectLabel(item[field], sdgs),
                            )}
                            placeholder={tabs[`${field}Placeholder`]}
                            onChange={({ value }) =>
                              updateSustainability({
                                index,
                                name: field,
                                value,
                                id: item.id,
                              })
                            }
                          />
                        ) : (
                          <Textarea
                            delay={700}
                            value={item[field]}
                            onChange={(event) =>
                              updateSustainability({
                                index,
                                name: field,
                                value: event.target.value,
                                id: item.id,
                              })
                            }
                            placeholder={tabs[`${field}Placeholder`]}
                          />
                        )
                      }
                    />
                  ))}
                </TabsList>
              ))}
            </div>

            {collection.length < SUSTAINABILITY_MAX_COUNT && (
              <div className="bmc-page-tabs-content-actions">
                <Button
                  variant="outline"
                  onClick={addSustainability}
                  className="bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
                >
                  <Plus className="w-7 h-auto" />
                  {tabs.addNew}
                </Button>
              </div>
            )}
          </div>,

          <Tabs
            key="tabs"
            tabs={caseStudies.map((e) => e.name)}
            className="bmc-page-tabs--black bmc-page-tabs--inner"
          >
            {caseStudies.map((item, index) => (
              <div key={index}>
                <TabsCut
                  setOpened={setOpened}
                  opened={opened}
                  caseStudy={item}
                />

                {/*   {item.sustainabilities.map((sustainability, i) => (
                  <TabsList key={i} horizontal sustainability>
                    {fields.map((field, ii) => (
                      <TabsListItem
                        key={ii}
                        title={tabs[field]}
                        text={sustainability[field]}
                        icon={iconsComponents[ii]}
                      />
                    ))}
                  </TabsList>
                ))} */}
                <table className="w-full ">
                  <thead>
                    <tr>
                      {caseStudyFields.map((field, i) => {
                        return (
                          <th className="w-1/3" key={field}>
                            <div
                              className={`flex flex-row items-center mb-6 ${
                                i !== caseStudyFields.length - 1 ? 'mr-10' : ''
                              }`}
                            >
                              <span className="p-2.5 bg-muted-lighter/10 rounded-md flex items-center justify-center mr-3">
                                {getTableHeaderIconByIndex(i)}
                              </span>
                              <span className="text-xl text-start">
                                {tabLabelForField(field)}
                              </span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {item.sustainabilities
                      .map((sustainabilityItem) => ({
                        ...sustainabilityItem,
                        sdgName:
                          sustainabilityItem.sdgName ||
                          selectLabel(sustainabilityItem.sdg, sdgs),
                      }))
                      .map((sustainabilityItem, i) => (
                        <tr key={i}>
                          {caseStudyFields.map((field) => (
                            <td key={field} className="text-start align-top">
                              <div
                                className={`mb-4 ${
                                  i !== sustainabilityItem.sustainabilities - 1
                                    ? 'mr-10'
                                    : ''
                                }`}
                              >
                                {sustainabilityItem[field]}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ))}
          </Tabs>,
        ]}
      </Tabs>

      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
}
